/* eslint-disable @typescript-eslint/no-floating-promises */
import { showNotification } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Check, ExclamationMark } from 'tabler-icons-react';

import { customAxios } from 'lib/axios';
import { Issue, Project } from 'types';
import { refetchUserOnError } from 'utils/refetchUserOnError';

type CreateIssueData = {
  project: string | undefined;
  title: string;
  type: 'task' | 'story' | 'bug' | 'epic' | 'sub-task';
  priority: 'low' | 'medium' | 'high';
  status: 'to do' | 'in progress' | 'in review' | 'deployment' | 'blocked' | 'done';
  description?: string;
  assignee?: string;
  jiraKey?: string;
  jiraUrl?: string;
  jiraStatus?: string;
  parentIssue?: string;
  epicLink?: string;
  createdBy?: string;
  // Jira-like fields
  labels?: string[];
  components?: string[];
  fixVersions?: string[];
  affectsVersions?: string[];
  environment?: 'development' | 'staging' | 'production';
  dueDate?: string; // ISO date string
  customFields?: Record<string, unknown>;
  watchers?: string[]; // Array of user IDs to watch the issue
  originalEstimateMinutes?: number; // Time estimate in minutes
  timeRemainingMinutes?: number; // Remaining time in minutes
};

const createIssue = async (issueData: CreateIssueData): Promise<Issue> => {
  if (typeof issueData.project === 'undefined') {
    return Promise.reject(new Error('Invalid projectId'));
  }

  const { data } = await customAxios.post<Issue>('/issues', issueData);
  return data;
};

type ProjectIssues = keyof Pick<
  Project,
  'todoIssues' | 'inProgressIssues' | 'inReviewIssues' | 'deploymentIssues' | 'blockedIssues' | 'completedIssues'
>;

const projectIssues: Record<Issue['status'], ProjectIssues> = {
  'to do': 'todoIssues',
  'in progress': 'inProgressIssues',
  'in review': 'inReviewIssues',
  deployment: 'deploymentIssues',
  blocked: 'blockedIssues',
  done: 'completedIssues',
};

export const useCreateIssue = (
  orgId: string | undefined,
  successCallback?: (createdIssue: Issue) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createIssue,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Project>(
        ['projects', variables.project],
        (old) => {
          if (old) {
            const copiedIssues = [...old[projectIssues[variables.status]]];

            return {
              ...old,
              [projectIssues[variables.status]]: [...copiedIssues, data],
            };
          }

          return old;
        }
      );

      queryClient.setQueryData(['issues', data._id], data);

      if (successCallback) {
        successCallback(data);
      }

      showNotification({
        title: 'Success',
        message: `Created: ${data.title}`,
        color: 'teal',
        icon: <Check />,
      });

      if (orgId) {
        queryClient.invalidateQueries({
          queryKey: ['org', orgId, 'issues'],
        });
      }

      // Invalidate all search queries to refresh My Tasks and other filtered views
      queryClient.invalidateQueries({
        predicate: (query) => {
          const { queryKey } = query;
          return (
            Array.isArray(queryKey) &&
            queryKey.length >= 2 &&
            queryKey[0] === 'issues' &&
            queryKey[1] === 'search'
          );
        },
      });
    },
    onError: (err) => {
      showNotification({
        title: 'Error',
        message: 'Failed to create issue. Please try again later.',
        color: 'red',
        icon: <ExclamationMark />,
      });

      refetchUserOnError(err, queryClient);
    },
  });
};
